import CustomTooltip from '../../../shared/CustomTooltip';
import { Edit as EditIcon } from '@mui/icons-material';
import { Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { format, isSameMonth, isToday } from 'date-fns';
import React, { Dispatch, SetStateAction } from 'react';
import {
    camelCaseToSentenceCase,
    formatCurrencyShort,
} from '../../../../shared/utils';
import {
    BaseExperienceCharge,
    ExperienceCharge,
    ExperiencePrice,
    FlexibleExperienceCharge,
    FlexiblePriceOverride,
    PriceOverride,
} from '../../../types/experience';
import { RateLabel } from './RatesCalendar';
import { useFormContext, useWatch } from 'react-hook-form';
import { FeeType } from '../../../enums/experience';

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type SelectionMode = 'range' | 'individual';

export const renderRateLabel = (name: string) => {
    if (name === FeeType.PerDeskPerDay) {
        return 'Desk';
    }
    if (name === FeeType.PerDeskPerHalfDay) {
        return 'Half day';
    }
    if (name === FeeType.PerOfficePerDay) {
        return 'Office';
    }
    if (name === FeeType.PerOfficePerHalfDay) {
        return 'Half day';
    }
    if (name === FeeType.PerMeetingRoomPerDay) {
        return 'Meeting room';
    }
    if (name === FeeType.PerMeetingRoomPerHalfDay) {
        return 'Half day';
    }
    if (name === FeeType.EntireVenuePerDay) {
        return 'Entire venue';
    }
    if (name === FeeType.EntireVenuePerHalfDay) {
        return 'Half day';
    }

    return camelCaseToSentenceCase(name);
};

type RatesMonthCalendarProps = {
    monthDate: Date;
    days: Date[];
    selectionMode: SelectionMode;
    rangeStart: Date | null;
    rangeEnd: Date | null;
    hoverDate: Date | null;
    charges: ExperienceCharge[];
    individualDates: Date[];
    setIndividualDates: Dispatch<SetStateAction<Date[]>>;
    setRangeStart: (date: Date | null) => void;
    setRangeEnd: (date: Date | null) => void;
    setHoverDate: (date: Date | null) => void;
    visibleMonth: Date;
    experienceType: string;
    isYearView: boolean;
};

const RatesMonthCalendar: React.FC<RatesMonthCalendarProps> = ({
    monthDate,
    days,
    selectionMode,
    rangeStart,
    rangeEnd,
    hoverDate,
    charges,
    individualDates,
    setIndividualDates,
    setRangeStart,
    setRangeEnd,
    setHoverDate,
    visibleMonth,
    experienceType,
    isYearView,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const weekdayFontSize = isYearView ? '0.65rem' : undefined;
    const stackSpacing = isYearView ? 0.25 : 0.5;
    const labelFontSize = isYearView ? '12px' : '13px';
    const compactRates = isYearView || isMobile;
    const shouldShowEllipsis = isYearView || isMobile;
    const dense = isYearView || isMobile;

    const { control } = useFormContext();

    const discounts = useWatch({ control, name: 'discounts' }) || [];

    const prices: ExperiencePrice[] = charges.filter(
        (charges) =>
            charges?.chargeType === 'price' ||
            charges?.chargeType === 'flexiblePrice',
    ) as ExperiencePrice[];

    const getMaxDiscountForDate = (date: Date): number | null => {
        const dateStr = format(date, 'yyyy-MM-dd');

        const applicableDiscounts = discounts
            .filter((discount: any) => !discount.disabled)
            .filter((discount: any) => {
                return discount.dates?.some((dateRange: any) => {
                    const startDate = dateRange.startDate;
                    const endDate = dateRange.endDate;
                    return dateStr >= startDate && dateStr <= endDate;
                });
            });

        if (applicableDiscounts.length === 0) {
            return null;
        }

        return Math.max(...applicableDiscounts.map((d: any) => d.percentage));
    };

    const flexiblePrice: FlexibleExperienceCharge | undefined = prices.find(
        (price) => price?.chargeType === 'flexiblePrice',
    ) as FlexibleExperienceCharge | undefined;

    type Override = {
        type: string;
        override: FlexiblePriceOverride | PriceOverride;
    };

    const minHireHours = useWatch({ control, name: 'minHireHours' });

    const getPricesToRender = (
        isWeekend: boolean,
        overrides: Override[],
    ): RateLabel[] => {
        if (flexiblePrice) {
            return [
                {
                    name: FeeType.PerDay,
                    value:
                        overrides.length > 0 &&
                        overrides[0].type === 'normal' &&
                        'dayPrice' in overrides[0]?.override
                            ? overrides[0].override.dayPrice
                            : isWeekend && flexiblePrice.weekendPrice?.dayPrice
                              ? flexiblePrice.weekendPrice?.dayPrice
                              : flexiblePrice.dayPrice,
                },
                {
                    name: FeeType.PerNight,
                    value:
                        overrides.length > 0 &&
                        'nightPrice' in overrides[0]?.override
                            ? overrides[0].override.nightPrice
                            : isWeekend &&
                                flexiblePrice.weekendPrice?.nightPrice
                              ? flexiblePrice.weekendPrice?.nightPrice
                              : flexiblePrice.nightPrice,
                },
                {
                    name: FeeType.PerHour,
                    value:
                        overrides.length > 0 &&
                        'hourPrice' in overrides[0]?.override
                            ? overrides[0]?.override.hourPrice
                            : isWeekend && flexiblePrice.weekendPrice?.hourPrice
                              ? flexiblePrice.weekendPrice?.hourPrice
                              : flexiblePrice.hourPrice,
                },
            ];
        } else if (experienceType === 'commercial') {
            return (prices as BaseExperienceCharge[]).map((price) => {
                const foundOverride =
                    overrides?.length > 0
                        ? overrides?.find((o) => o?.type === price?.type)
                        : null;
                return {
                    name: price.type,
                    value:
                        foundOverride && 'unitPrice' in foundOverride?.override
                            ? foundOverride?.override.unitPrice
                            : isWeekend && price.weekendPrice
                              ? price.weekendPrice.unitPrice
                              : price.unitPrice,
                };
            });
        }
        return (prices as BaseExperienceCharge[]).map((price) => ({
            name: price.type,
            value:
                overrides[0] && 'unitPrice' in overrides[0]?.override
                    ? overrides[0]?.override.unitPrice
                    : isWeekend && price.weekendPrice
                      ? price.weekendPrice.unitPrice
                      : price.unitPrice,
        }));
    };

    const getOverrideForDate = (date: Date): Override[] => {
        const dateString = format(date, 'yyyy-MM-dd');

        if (experienceType === 'commercial') {
            const overridesForEachPrice = prices.map((price) => {
                const priceType =
                    (price as BaseExperienceCharge)?.type ?? 'normal';
                const priceOverrides = price.priceOverrides as
                    | (FlexiblePriceOverride | PriceOverride)[]
                    | undefined;

                if (!priceOverrides?.length) {
                    return null;
                }

                const overrideForDate = priceOverrides.find(
                    (o: FlexiblePriceOverride | PriceOverride) =>
                        o.date === dateString,
                );

                if (!overrideForDate) {
                    return null;
                }

                return {
                    type: priceType,
                    override: overrideForDate,
                };
            });

            return overridesForEachPrice.filter(Boolean) as Override[];
        }

        const overrides = prices[0]?.priceOverrides as
            | (FlexiblePriceOverride | PriceOverride)[]
            | undefined;

        if (!overrides?.length) {
            return [];
        }

        const override = overrides.find(
            (o: FlexiblePriceOverride | PriceOverride) => o.date === dateString,
        );

        return override ? [{ type: 'normal', override }] : [];
    };

    const isDateInRange = (date: Date) => {
        if (selectionMode === 'individual') {
            return individualDates.some(
                (d) => format(d, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'),
            );
        }

        if (!rangeStart) return false;

        const end = rangeEnd || (hoverDate && !rangeEnd ? hoverDate : null);
        if (!end) {
            return (
                format(date, 'yyyy-MM-dd') === format(rangeStart, 'yyyy-MM-dd')
            );
        }

        const start = rangeStart < end ? rangeStart : end;
        const endDate = rangeStart < end ? end : rangeStart;

        const dateStr = format(date, 'yyyy-MM-dd');
        const startStr = format(start, 'yyyy-MM-dd');
        const endStr = format(endDate, 'yyyy-MM-dd');

        return dateStr >= startStr && dateStr <= endStr;
    };

    const handleDateClick = (date: Date) => {
        if (!isSameMonth(date, visibleMonth)) return;

        if (selectionMode === 'individual') {
            const dateStr = format(date, 'yyyy-MM-dd');
            const isSelected = individualDates.some(
                (d) => format(d, 'yyyy-MM-dd') === dateStr,
            );

            if (isSelected) {
                setIndividualDates((prev: any[]) =>
                    prev.filter((d) => format(d, 'yyyy-MM-dd') !== dateStr),
                );
            } else {
                setIndividualDates((prev: any[]) => [...prev, date]);
            }
        } else {
            if (!rangeStart || rangeEnd) {
                setRangeStart(date);
                setRangeEnd(null);
                setHoverDate(null);
            } else {
                setRangeEnd(date);
                setHoverDate(null);
            }
        }
    };

    const handleDateHover = (date: Date) => {
        if (!isSameMonth(date, visibleMonth)) return;
        if (selectionMode === 'range' && rangeStart && !rangeEnd) {
            setHoverDate(date);
        }
    };

    const isWeekend = (date: Date, weekendDays: string[]): boolean => {
        const dayNames = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ];

        const dayName = dayNames[date.getDay()];

        return weekendDays.includes(dayName);
    };

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <Grid container columns={7} spacing={dense ? 0 : 1}>
                {weekdayLabels.map((weekday) => (
                    <Grid
                        item
                        xs={1}
                        key={`${monthDate.toISOString()}-${weekday}-label`}
                    >
                        <Typography
                            variant='caption'
                            sx={{
                                color: 'text.primary',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                fontSize: weekdayFontSize,
                            }}
                        >
                            {dense ? weekday[0] : weekday}
                        </Typography>
                    </Grid>
                ))}

                {days.map((day, index) => {
                    const outsideCurrentMonth = !isSameMonth(day, monthDate);
                    const override = getOverrideForDate(day);

                    const hasOverride =
                        !outsideCurrentMonth && override.length > 0;
                    const inRange = isDateInRange(day);
                    const weekendDays = prices[0]?.weekendPrice?.days ?? [];
                    const isWeekendDay = isWeekend(day, weekendDays);
                    const isPreviewing =
                        selectionMode === 'range' &&
                        rangeStart &&
                        !rangeEnd &&
                        hoverDate;

                    const pricesForDate = getPricesToRender(
                        isWeekendDay,
                        override,
                    );

                    const slicedPrices = dense
                        ? pricesForDate.slice(0, 1)
                        : pricesForDate;
                    const maxDiscount = getMaxDiscountForDate(day);
                    const colIndex = index % 7;
                    const totalRows = Math.ceil(days.length / 7);
                    const rowIndex = Math.floor(index / 7);
                    const isFirstRow = rowIndex === 0;
                    const isLastRow = rowIndex === totalRows - 1;
                    const isFirstCol = colIndex === 0;
                    const isLastCol = colIndex === 6;

                    return (
                        <Grid
                            item
                            xs={1}
                            key={`${monthDate.toISOString()}-${day.toISOString()}`}
                            sx={{
                                minHeight: '70px',
                            }}
                        >
                            <Box
                                onClick={() =>
                                    !outsideCurrentMonth && handleDateClick(day)
                                }
                                onMouseEnter={() =>
                                    !outsideCurrentMonth && handleDateHover(day)
                                }
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    border: '1px solid',

                                    borderColor: dense
                                        ? 'divider'
                                        : inRange
                                          ? 'primary.main'
                                          : hasOverride
                                            ? 'primary.main'
                                            : 'divider',

                                    borderRightColor: dense
                                        ? isLastCol
                                            ? 'divider'
                                            : inRange
                                              ? alpha(
                                                    theme.palette.primary.main,
                                                    isPreviewing ? 0.1 : 0.2,
                                                )
                                              : hasOverride
                                                ? alpha(
                                                      theme.palette.primary
                                                          .main,
                                                      0.05,
                                                  )
                                                : 'primary.contrastText'
                                        : undefined,
                                    borderBottomColor: dense
                                        ? isLastRow
                                            ? 'divider'
                                            : inRange
                                              ? alpha(
                                                    theme.palette.primary.main,
                                                    isPreviewing ? 0.1 : 0.2,
                                                )
                                              : hasOverride
                                                ? alpha(
                                                      theme.palette.primary
                                                          .main,
                                                      0.05,
                                                  )
                                                : 'primary.contrastText'
                                        : undefined,

                                    borderRadius: dense ? undefined : 1,
                                    borderTopLeftRadius:
                                        dense && isFirstRow && isFirstCol
                                            ? 5
                                            : undefined,
                                    borderTopRightRadius:
                                        dense && isFirstRow && isLastCol
                                            ? 5
                                            : undefined,
                                    borderBottomLeftRadius:
                                        dense && isLastRow && isFirstCol
                                            ? 5
                                            : undefined,
                                    borderBottomRightRadius:
                                        dense && isLastRow && isLastCol
                                            ? 5
                                            : undefined,

                                    p: dense ? 0.5 : 0.7,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: dense ? 0.25 : 0.5,
                                    bgcolor: inRange
                                        ? alpha(
                                              theme.palette.primary.main,
                                              isPreviewing ? 0.1 : 0.2,
                                          )
                                        : hasOverride
                                          ? alpha(
                                                theme.palette.primary.main,
                                                0.05,
                                            )
                                          : 'background.paper',
                                    opacity: outsideCurrentMonth ? 0.5 : 1,
                                    cursor: !outsideCurrentMonth
                                        ? 'pointer'
                                        : 'default',
                                    position: 'relative',
                                    transition: 'all 0.2s',
                                    userSelect: 'none',
                                    '&:hover': !outsideCurrentMonth
                                        ? {
                                              borderColor: 'primary.main',
                                              boxShadow: 1,
                                          }
                                        : {},
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        zIndex: 1,
                                        gap: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            color: isToday(day)
                                                ? 'primary.main'
                                                : 'text.primary',
                                            fontSize: labelFontSize,
                                        }}
                                    >
                                        {format(day, 'd')}
                                    </Typography>
                                    {hasOverride && (
                                        <EditIcon
                                            sx={{
                                                fontSize: dense
                                                    ? '0.65rem'
                                                    : '0.8rem',
                                                color: 'primary.main',
                                                mb: 0.2,
                                            }}
                                        />
                                    )}
                                </Box>
                                {maxDiscount !== null && (
                                    <Typography
                                        variant='h4'
                                        sx={{
                                            position: 'absolute',
                                            top: dense ? undefined : 2,
                                            bottom: dense ? 2 : undefined,
                                            right: 2,
                                            fontSize: '10px',
                                            p: '2px',
                                            borderRadius: '4px',
                                            opacity: 0.75,
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                        }}
                                    >
                                        -{maxDiscount}%
                                    </Typography>
                                )}
                                <Stack
                                    spacing={stackSpacing}
                                    sx={{
                                        mt: 'auto',
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    {slicedPrices
                                        .filter((price) => {
                                            return minHireHours < 24
                                                ? true
                                                : !price.name.includes(
                                                      'HalfDay',
                                                  );
                                        })
                                        .map((price: RateLabel) => {
                                            const isHalfDay = [
                                                FeeType.PerDeskPerHalfDay,
                                                FeeType.PerOfficePerHalfDay,
                                                FeeType.PerMeetingRoomPerHalfDay,
                                                FeeType.EntireVenuePerHalfDay,
                                            ].includes(price.name);
                                            return (
                                                <Box
                                                    key={price.name}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent:
                                                            'space-between',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            lineHeight: 1.2,
                                                            fontSize: isHalfDay
                                                                ? '12px'
                                                                : labelFontSize,
                                                            color: isHalfDay
                                                                ? 'text.secondary'
                                                                : 'text.primary',
                                                            ml: isHalfDay
                                                                ? 1
                                                                : 0,
                                                        }}
                                                    >
                                                        {renderRateLabel(
                                                            price.name,
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            lineHeight: 1.2,
                                                            textAlign: 'left',

                                                            fontSize:
                                                                labelFontSize,
                                                        }}
                                                    >
                                                        {compactRates
                                                            ? '$' + price.value
                                                            : formatCurrencyShort(
                                                                  price.value,
                                                              )}
                                                    </Typography>
                                                    {shouldShowEllipsis &&
                                                        pricesForDate.length >
                                                            1 && (
                                                            <Typography
                                                                variant='caption'
                                                                sx={{
                                                                    color: 'text.primary',
                                                                    lineHeight: 1.2,
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                ...
                                                            </Typography>
                                                        )}
                                                </Box>
                                            );
                                        })}
                                </Stack>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default RatesMonthCalendar;
